/**
 * Fair Home V2 — FE-004: wheel arbitration between a slide's inner scrollport
 * and the pager.
 *
 * On short viewports (`@media (max-height: 860px)`, see `fair-home-v2.css`) a
 * slide that does not fit 100dvh becomes scrollable instead of clipping its
 * bottom. Swiper's mousewheel module listens on the pager container, so without
 * this the first wheel tick would page away and the overflowing part would still
 * be unreachable. Here the wheel event is stopped from reaching Swiper while the
 * slide has room left to scroll in that direction; at either edge it propagates
 * and the pager takes over as usual.
 */
import { onBeforeUnmount, onMounted, type Ref } from 'vue'

/** Sub-pixel slack: scrollHeight/clientHeight rounding must not fake an edge. */
const EDGE_EPSILON_PX = 1

export function useFairHomeV2SlideScroll(rootRef: Ref<HTMLElement | null>) {
  function onWheelCapture(event: WheelEvent) {
    const target = event.target as HTMLElement | null
    const scrollport = target?.closest<HTMLElement>('.fair-home-v2__slide-inner')
    if (!scrollport) return

    const overflow = scrollport.scrollHeight - scrollport.clientHeight
    if (overflow <= EDGE_EPSILON_PX) return

    const goingDown = event.deltaY > 0
    const atTop = scrollport.scrollTop <= EDGE_EPSILON_PX
    const atBottom = scrollport.scrollTop >= overflow - EDGE_EPSILON_PX

    // At the edge in the wheel's direction the pager should page as usual.
    if (goingDown ? atBottom : atTop) return

    // Native scrolling stays — only Swiper is kept out of it.
    event.stopPropagation()
  }

  onMounted(() => {
    rootRef.value?.addEventListener('wheel', onWheelCapture, {
      capture: true,
      passive: true,
    })
  })

  onBeforeUnmount(() => {
    rootRef.value?.removeEventListener('wheel', onWheelCapture, {
      capture: true,
    } as EventListenerOptions)
  })
}
